import FileExcel from '~/assets/icons/file-excel.svg'
import FilePDF from '~/assets/icons/file-pdf.svg'
import FilePowerPoint from '~/assets/icons/file-powerpoint.svg'
import FileVideo from '~/assets/icons/file-video.svg'
import FileWord from '~/assets/icons/file-word.svg'
import FileDefault from '~/assets/icons/insert_drive_file.svg'

export const CHAT_IMAGE_PREVIEW_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.img', '.webp', '.heic', '.heif'] as const

const MIME_BY_EXT: Record<string, string> = {
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.pdf': 'application/pdf',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.jfif': 'image/jpeg',
    '.img': 'image/jpeg',
    '.gif': 'image/gif',
    '.webp': 'image/webp',
    '.bmp': 'image/bmp',
    '.dib': 'image/bmp',
    '.tif': 'image/tiff',
    '.tiff': 'image/tiff',
    '.heic': 'image/heic',
    '.heif': 'image/heif',
    '.avif': 'image/avif',
    '.svg': 'image/svg+xml',
    '.ico': 'image/x-icon',
    '.raw': 'image/x-raw',
    '.avi': 'video/x-msvideo',
    '.mp4': 'video/mp4',
    '.mwv': 'video/x-ms-wmv',
    '.wmv': 'video/x-ms-wmv',
    '.webm': 'video/webm',
    '.3gp': 'video/3gpp',
}

const EXT_BY_MIME: Record<string, string> = {
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/pdf': 'pdf',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
    'image/tiff': 'tiff',
    'video/mp4': 'mp4',
    'video/x-msvideo': 'avi',
    'video/x-ms-wmv': 'wmv',
    'video/webm': 'webm',
    'video/3gpp': '3gp',
}

type AttachmentIconGroup = {
    extensions: string[]
    icon: string
}

const ATTACHMENT_ICON_GROUPS: AttachmentIconGroup[] = [
    {
        extensions: ['3g2', '3gp', 'asf', 'avi', 'flv', 'm4v', 'mov', 'mp4', 'mpg', 'rm', 'srt', 'swf', 'vob', 'wmv', 'webm'],
        icon: FileVideo,
    },
    {
        extensions: ['pptx', 'pptm', 'ppt', 'xps', 'potx', 'pot', 'ppsx', 'ppsm', 'pps', 'ppam', 'ppa'],
        icon: FilePowerPoint,
    },
    { extensions: ['pdf'], icon: FilePDF },
    { extensions: ['doc', 'docx'], icon: FileWord },
    {
        extensions: ['csv', 'xlr', 'xls', 'xlsx', 'xlsm', 'xlsb', 'xltx', 'xltm', 'xlt', 'xml', 'xla'],
        icon: FileExcel,
    },
]

export const normalizeExtensions = (exts: string[]): string[] => {
    return exts.map(ext => {
        const trimmed = String(ext).trim().toLowerCase()
        if (!trimmed) {
            return ''
        }
        return trimmed.startsWith('.') ? trimmed : `.${trimmed}`
    }).filter(Boolean)
}


export const isAllowedChatExtension = (ext: string, allowedExts: string[]): boolean => {
    if (!allowedExts || allowedExts.length === 0) {
        return true
    }
    const normalizedExt = ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`
    return normalizeExtensions(allowedExts).includes(normalizedExt)
}

export const buildAcceptFromExtensions = (exts: string[]): string => normalizeExtensions(exts).join(',')

export const getMimeTypeForExtension = (ext: string): string => {
    const normalized = ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`
    return MIME_BY_EXT[normalized] || ''
}

export const getExtensionFromName = (nameOrUrl: string): string => {
    const withoutQuery = (nameOrUrl || '').split('?')[0]
    const base = withoutQuery.split('/').pop() || withoutQuery
    const dot = base.lastIndexOf('.')
    return dot >= 0 ? base.slice(dot).toLowerCase() : ''
}

export const getExtensionFromMimeType = (mimeType: string, withDot: boolean = false): string => {
    const cleaned = (mimeType || '').split(';')[0].trim().toLowerCase()
    const ext = EXT_BY_MIME[cleaned] || ''
    if (!ext) {
        return ''
    }
    return withDot ? `.${ext}` : ext
}

export const isChatImageExtension = (ext: string): boolean => {
    const normalized = ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`
    return (CHAT_IMAGE_PREVIEW_EXTENSIONS as readonly string[]).includes(normalized)
}

export const isChatImageUrl = (url: string): boolean =>
    isChatImageExtension(getExtensionFromName(url))

export const getAttachmentFileName = (url: string): string => {
    const withoutQuery = (url || '').split('?')[0]
    let raw: string
    try {
        raw = decodeURIComponent(withoutQuery.split('/').pop() || 'document')
    } catch {
        raw = withoutQuery.split('/').pop() || 'document'
    }

    const dot = raw.lastIndexOf('.')
    if (dot <= 0) {
        return raw
    }
    const base = raw.slice(0, dot)
    const ext = raw.slice(dot)
    const stripped = base.replace(/(_\d+)+$/, '')
    return (stripped || base) + ext
}

export const getMimeTypeForUrl = (url: string): string => {
    const ext = getExtensionFromName(url)
    if (MIME_BY_EXT[ext]) {
        return MIME_BY_EXT[ext]
    }
    return 'application/octet-stream'
}

export const getAttachmentIconSrc = (nameOrUrlOrExt: string): string => {
    const raw = (nameOrUrlOrExt || '').toLowerCase()
    const ext = raw.startsWith('.')
        ? raw.slice(1)
        : getExtensionFromName(nameOrUrlOrExt).replace(/^\./, '')
    const group = ATTACHMENT_ICON_GROUPS.find(g => g.extensions.includes(ext))
    return group?.icon ?? FileDefault
}

export const splitAttachmentUrls = (urls: string[]): { imageUrls: string[]; documentUrls: string[] } => {
    const imageUrls: string[] = []
    const documentUrls: string[] = []
    for (const url of urls) {
        if (!url) {
            continue
        }
        if (isChatImageUrl(url)) {
            imageUrls.push(url)
        } else {
            documentUrls.push(url)
        }
    }
    return { imageUrls, documentUrls }
}
